import { useRef, type FC } from 'react'

import type { ComboboxRef } from '@skytech/ui'
import { Combobox, Input, LoaderCircle, Select } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { ADMIN_ROLES, USER_ROLE, useHasRoleBasedAccess } from '@/hooks/access'

import queries from '../../queries'
import adminResources from '../../resources'
import type { Customer, Employee } from '../../resources'

export const UserInfo: FC<{ selectedCustomerId: number | undefined; isCustomerAdmin: boolean }> = ({
  selectedCustomerId,
  isCustomerAdmin,
}) => {
  const comboboxMethodsRef = useRef<ComboboxRef>(null)
  const translate = useTranslation()
  const hasLocationsAccess = useHasRoleBasedAccess([...ADMIN_ROLES, USER_ROLE.IT_USER])

  const { data: customers, isLoading: isCustomersLoading } = useQuery(
    queries.adminCustomers(),
    adminResources.getCustomers
  )
  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)
  const { data: employees, isLoading: isEmployeesLoading } = useQuery(
    queries.adminEmployeeList(),
    () => adminResources.getEmployees(hasLocationsAccess)
  )

  const employeeFiltered =
    employees?.filter(employee => {
      const isVisible = !employee.isHidden
      const isSelectable =
        employee.customerId === selectedCustomerId || !employee.customerId || !selectedCustomerId
      return isVisible && isSelectable
    }) || []

  if (isCustomersLoading || isEmployeesLoading) {
    return (
      <div className="mx-2 grid grid-cols-2 gap-4 rounded-lg">
        <LoaderCircle />
      </div>
    )
  }

  if (isCustomersLoading || isEmployeesLoading) {
    return (
      <div className="mx-2 grid grid-cols-2 gap-4 rounded-lg">
        <LoaderCircle />
      </div>
    )
  }

  const locationUserTypeId = assetTypes?.userTypes.find(type => type.type === 'LOCATION')?.id

  return (
    <div className="mx-2 grid grid-cols-2 gap-4 rounded-lg">
      {!isCustomerAdmin && (
        <Field name="customerName">
          {({ field, meta, form }: FieldProps) => (
            <Combobox
              id="customerName"
              capitalizeLabel
              isFullWidth
              hitsTranslation={<Translation id="hit(s)" />}
              noResultsFoundTranslation={<Translation id="No results found" />}
              marginBottom={16}
              label={
                <>
                  <Translation id="Company" />*
                </>
              }
              ariaLabel={translate('Company')}
              list={customers || []}
              stringMatcher={(item: Customer | null) => (item ? `${item.name} ${item.orgNo}` : '')}
              touched={meta.touched}
              error={meta.error}
              placeholder={translate('Choose company...')}
              onSetFieldTouched={() => form.setFieldTouched('customerName')}
              {...field}
              onSetFieldValue={(v: string) => {
                form.setFieldValue('customerName', v)
              }}
              onCustomChange={() => {
                form.setFieldValue('userName', '')
                form.setFieldTouched('userName')
                comboboxMethodsRef.current?.onResetCombobox()
              }}
            />
          )}
        </Field>
      )}

      <Field name="userName">
        {({ field, meta, form }: FieldProps) => (
          <Combobox
            isFullWidth
            capitalizeLabel
            comboboxMethodsRef={comboboxMethodsRef}
            id="userName"
            hitsTranslation={<Translation id="hit(s)" />}
            noResultsFoundTranslation={<Translation id="No results found" />}
            marginBottom={16}
            label={
              <>
                <Translation id="Username" />*
              </>
            }
            ariaLabel={translate('Username')}
            list={employeeFiltered}
            stringMatcher={(item: Employee | null) => (item ? `${item.name} - ${item.code}` : '')}
            touched={meta.touched}
            error={meta.error}
            placeholder={translate('Choose employee...')}
            onSetFieldValue={(v: string) => {
              form.setFieldValue('userName', v)
              if (!v) return
              // We display username and phone number but send only the username
              const parsedUserName = v.split(' -')[0]
              const selectedEmployee = employees?.find(employee => employee.name === parsedUserName)
              const isLocationUser = selectedEmployee?.isLocation
              if (!isLocationUser) return
              if (typeof locationUserTypeId !== 'number')
                throw Error('User type id is not a number!')
              form.setFieldValue('userTypeId', locationUserTypeId)
              form.setFieldValue('customerAddressId', selectedEmployee.customerAddressId)
            }}
            onSetFieldTouched={() => form.setFieldTouched('userName')}
            {...field}
          />
        )}
      </Field>

      <Field name="userTypeId">
        {({ field, form }: FieldProps) => (
          <Select
            capitalizeLabel
            id="userTypeId"
            label={
              <>
                <Translation id="User type" />*
              </>
            }
            options={
              assetTypes?.userTypes.map(({ id, type }) => ({
                value: id,
                label: translate(`asset_user_type/${type}`),
              })) || []
            }
            marginBottom={16}
            {...field}
            onChange={option => form.setFieldValue(field.name, option?.value)}
          />
        )}
      </Field>

      <Field name="received">
        {({ field, meta }: FieldProps) => (
          <Input
            capitalizeLabel
            isFullWidth
            id="received"
            ariaLabel={translate('Received')}
            label={
              <>
                <Translation id="Received" />*
              </>
            }
            marginBottom={16}
            touched={meta.touched}
            error={meta.error}
            type="date"
            className="cursor-text"
            {...field}
          />
        )}
      </Field>
    </div>
  )
}
